import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import FileUpload from '@/components/FileUpload';
import FileViewer from '@/components/FileViewer';
import { Send, MessageCircle, Users, CheckCheck, ArrowLeft, Megaphone, UsersRound, Pencil, Trash2, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { admin: 'Administrateur', chef_poste: 'Chef de Poste', agent: 'Agent', site: 'Site', client: 'Client' };
const ROLE_ORDER = ['admin', 'chef_poste', 'agent', 'site', 'client'];
const sortByRoleOrder = (list) =>
  [...list].sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role));

const MessagerieEvoluee = ({ user, onLogout }) => {
  const location = useLocation();
  const canUsePrivate = user?.permission_overrides?.use_messagerie ?? (user?.role !== 'site');
  const canWriteChannel = user?.permission_overrides?.write_channel_messagerie ?? (user?.role !== 'site');
  const canViewAnnouncements = user?.permission_overrides?.view_announcements ?? ['admin', 'chef_poste', 'agent', 'site'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const canSeeAnnouncementReadStatus = ['admin', 'chef_poste'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('channels');
  const [notifCounts, setNotifCounts] = useState({ unread_messages: 0, unread_channel_messages: 0, unread_announcements: 0 });
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [channelMessages, setChannelMessages] = useState([]);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [newChannelMessage, setNewChannelMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const [activeContactId, setActiveContactId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [newConversationTarget, setNewConversationTarget] = useState('');

  // Communication Entreprise (annonces diffusées par les admins)
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementContent, setAnnouncementContent] = useState('');
  const [announcementTag, setAnnouncementTag] = useState('');
  const [announcementTagColor, setAnnouncementTagColor] = useState('#4F46E5');
  const [announcementAttachments, setAnnouncementAttachments] = useState([]);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState(null);
  const [viewingAnnouncementId, setViewingAnnouncementId] = useState(null);

  // Envoi groupé d'un même message privé (plusieurs personnes, ou un rôle
  // en particulier d'un site en particulier)
  const [showBulkCompose, setShowBulkCompose] = useState(false);
  const [bulkMode, setBulkMode] = useState('people');
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkRoles, setBulkRoles] = useState([]);
  const [bulkSites, setBulkSites] = useState([]);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkContent, setBulkContent] = useState('');
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => {
    loadData();
    loadNotifCounts();
    if (canViewAnnouncements) loadAnnouncements();
    const interval = setInterval(() => {
      if (selectedSite) loadChannelMessages(selectedSite);
      loadPrivateMessages();
      loadNotifCounts();
      if (canViewAnnouncements) loadAnnouncements();
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedSite]);

  // Arrivée depuis la cloche de notification : ouvre directement la conversation
  // précise plutôt que la messagerie générale.
  useEffect(() => {
    if (location.state?.openContactId) {
      setActiveTab('private');
      openConversation(location.state.openContactId);
    } else if (location.state?.openTab) {
      setActiveTab(location.state.openTab);
    }
    // eslint-disable-next-line
  }, [location.state, privateMessages.length]);

  const loadNotifCounts = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setNotifCounts(await res.json());
    } catch { /* silencieux */ }
  };

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [sitesRes, usersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/sites`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/messaging/contacts`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (sitesRes.ok) {
        const sitesData = await sitesRes.json();
        setSites(sitesData);
        if (sitesData.length > 0 && !selectedSite) {
          setSelectedSite(sitesData[0].id);
          loadChannelMessages(sitesData[0].id);
        }
      }

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.filter(u => u.id !== user.id));
      }

      loadPrivateMessages();
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadChannelMessages = async (siteId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/channels/${siteId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChannelMessages(data);
        data.forEach(msg => {
          if (!msg.read_by.includes(user.id) && msg.from_user_id !== user.id) {
            markChannelMessageRead(msg.id);
          }
        });
      }
    } catch (error) {
      console.error('Error loading channel messages:', error);
    }
  };

  const loadPrivateMessages = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPrivateMessages(data);
      }
    } catch (error) {
      console.error('Error loading private messages:', error);
    }
  };

  const sendChannelMessage = async () => {
    if (!newChannelMessage.trim() || !selectedSite) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/channels/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSite, content: newChannelMessage, attachments: [] })
      });
      if (response.ok) {
        setNewChannelMessage('');
        loadChannelMessages(selectedSite);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur d\'envoi');
      }
    } catch {
      toast.error('Erreur d\'envoi');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeContactId) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_user_id: activeContactId, subject: '(suite de conversation)', content: replyText })
      });
      if (response.ok) {
        setReplyText('');
        loadPrivateMessages();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur d\'envoi');
      }
    } catch {
      toast.error('Erreur d\'envoi');
    }
  };

  const markChannelMessageRead = async (messageId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${BACKEND_URL}/api/channels/messages/${messageId}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markPrivateMessageRead = async (messageId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${BACKEND_URL}/api/messages/${messageId}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const loadAnnouncements = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAnnouncements(await res.json());
    } catch { /* silencieux */ }
  };

  const markAnnouncementRead = async (announcementId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${BACKEND_URL}/api/announcements/${announcementId}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* silencieux */ }
  };

  // La mention "lu" n'est posée qu'à l'ouverture effective de l'annonce (pas
  // dès l'affichage de la liste), pour refléter une vraie lecture du texte.
  const openAnnouncement = (a) => {
    setViewingAnnouncementId(a.id);
    if (!a.read_by.includes(user.id)) {
      markAnnouncementRead(a.id).then(loadAnnouncements);
    }
  };

  const resetAnnouncementForm = () => {
    setShowAnnouncementForm(false);
    setEditingAnnouncementId(null);
    setAnnouncementTitle('');
    setAnnouncementContent('');
    setAnnouncementTag('');
    setAnnouncementTagColor('#4F46E5');
    setAnnouncementAttachments([]);
  };

  const openEditAnnouncement = (a) => {
    setEditingAnnouncementId(a.id);
    setAnnouncementTitle(a.title);
    setAnnouncementContent(a.content);
    setAnnouncementTag(a.tag || '');
    setAnnouncementTagColor(a.tag_color || '#4F46E5');
    setAnnouncementAttachments(a.attachments || []);
    setShowAnnouncementForm(true);
  };

  const sendAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementContent.trim()) {
      toast.error('Le titre et le contenu sont obligatoires');
      return;
    }
    setSendingAnnouncement(true);
    const token = localStorage.getItem('token');
    try {
      const url = editingAnnouncementId
        ? `${BACKEND_URL}/api/announcements/${editingAnnouncementId}`
        : `${BACKEND_URL}/api/announcements`;
      const res = await fetch(url, {
        method: editingAnnouncementId ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: announcementTitle.trim(),
          content: announcementContent.trim(),
          tag: announcementTag.trim() || null,
          tag_color: announcementTag.trim() ? announcementTagColor : null,
          attachments: announcementAttachments
        })
      });
      if (res.ok) {
        toast.success(editingAnnouncementId ? 'Annonce modifiée' : 'Annonce publiée');
        resetAnnouncementForm();
        loadAnnouncements();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la publication');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Supprimer définitivement cette annonce ?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Annonce supprimée');
        loadAnnouncements();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la suppression');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  // Personnes correspondant aux rôles + sites choisis (un ou plusieurs de
  // chaque), pour l'envoi groupé "par rôle et site" (dérivé de la liste de
  // contacts déjà autorisés pour cet utilisateur - mêmes règles de contact).
  const bulkRoleSiteMatches = (bulkRoles.length === 0 || bulkSites.length === 0) ? [] : users.filter(u =>
    bulkRoles.includes(u.role) && (u.assigned_sites || []).some(s => bulkSites.includes(s))
  );

  const openBulkCompose = () => {
    setBulkMode('people');
    setBulkSelectedIds([]);
    setBulkRoles([]);
    setBulkSites([]);
    setBulkSubject('');
    setBulkContent('');
    setShowBulkCompose(true);
  };

  const toggleBulkSelected = (userId) => {
    setBulkSelectedIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const toggleBulkRole = (role) => {
    setBulkRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const toggleBulkSite = (siteId) => {
    setBulkSites(prev => prev.includes(siteId) ? prev.filter(s => s !== siteId) : [...prev, siteId]);
  };

  const sendBulkMessage = async () => {
    const recipientIds = bulkMode === 'people' ? bulkSelectedIds : bulkRoleSiteMatches.map(u => u.id);
    if (recipientIds.length === 0) {
      toast.error('Sélectionnez au moins un destinataire');
      return;
    }
    if (!bulkContent.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }
    setBulkSending(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/messages/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_user_ids: recipientIds,
          subject: bulkSubject.trim() || '(message groupé)',
          content: bulkContent.trim()
        })
      });
      if (res.ok) {
        const sent = await res.json();
        toast.success(`Message envoyé à ${sent.length} destinataire${sent.length > 1 ? 's' : ''}`);
        setShowBulkCompose(false);
        loadPrivateMessages();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'envoi');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setBulkSending(false);
    }
  };

  const conversations = useMemo(() => {
    const byContact = {};
    privateMessages.forEach(msg => {
      const contactId = msg.from_user_id === user.id ? msg.to_user_id : msg.from_user_id;
      const contactName = msg.from_user_id === user.id ? msg.to_user_name : msg.from_user_name;
      if (!byContact[contactId]) {
        byContact[contactId] = { id: contactId, name: contactName, lastMessage: msg, unread: 0 };
      }
      if (new Date(msg.created_at) > new Date(byContact[contactId].lastMessage.created_at)) {
        byContact[contactId].lastMessage = msg;
      }
      if (msg.to_user_id === user.id && !msg.read) {
        byContact[contactId].unread += 1;
      }
    });
    users.forEach(u => {
      if (byContact[u.id]) {
        byContact[u.id].role = u.role;
      }
    });
    return Object.values(byContact).sort((a, b) => {
      if (!a.lastMessage) return 1;
      if (!b.lastMessage) return -1;
      return new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at);
    });
  }, [privateMessages, users, user.id]);

  const activeMessages = useMemo(() => {
    if (!activeContactId) return [];
    return privateMessages
      .filter(m => m.from_user_id === activeContactId || m.to_user_id === activeContactId)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [privateMessages, activeContactId]);

  const activeContact = conversations.find(c => c.id === activeContactId);
  const viewingAnnouncement = announcements.find(a => a.id === viewingAnnouncementId) || null;

  const openConversation = (contactId) => {
    setActiveContactId(contactId);
    const toMark = privateMessages.filter(m => m.from_user_id === contactId && m.to_user_id === user.id && !m.read);
    toMark.forEach(m => markPrivateMessageRead(m.id));
    if (toMark.length > 0) {
      setTimeout(loadNotifCounts, 300);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in" data-testid="messagerie-evoluee">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Messagerie
          </h1>
          <p className="text-gray-600 dark:text-slate-300">Canaux par site et messages privés</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${(() => {
            const tabCount = 1 + (canUsePrivate ? 1 : 0) + (canViewAnnouncements ? 1 : 0);
            return tabCount === 3 ? 'grid-cols-3' : tabCount === 2 ? 'grid-cols-2' : 'grid-cols-1';
          })()}`}>
            <TabsTrigger value="channels" data-testid="tab-channels" className="relative px-1.5 sm:px-3 text-xs sm:text-sm">
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">
                <span className="sm:hidden">Canaux</span>
                <span className="hidden sm:inline">Canaux de Site</span>
              </span>
              {notifCounts.unread_channel_messages > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-red-500 text-white h-5 min-w-5 flex items-center justify-center px-1.5 shrink-0">
                  {notifCounts.unread_channel_messages}
                </Badge>
              )}
            </TabsTrigger>
            {canUsePrivate && (
              <TabsTrigger value="private" data-testid="tab-private" className="relative px-1.5 sm:px-3 text-xs sm:text-sm">
                <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
                <span className="truncate">
                  <span className="sm:hidden">Privés</span>
                  <span className="hidden sm:inline">Messages Privés</span>
                </span>
                {notifCounts.unread_messages > 0 && (
                  <Badge className="ml-1 sm:ml-2 bg-red-500 text-white h-5 min-w-5 flex items-center justify-center px-1.5 shrink-0">
                    {notifCounts.unread_messages}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            {canViewAnnouncements && (
              <TabsTrigger value="announcements" data-testid="tab-announcements" className="relative px-1.5 sm:px-3 text-xs sm:text-sm">
                <Megaphone className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
                <span className="truncate">
                  <span className="sm:hidden">Annonces</span>
                  <span className="hidden sm:inline">Communication Entreprise</span>
                </span>
                {notifCounts.unread_announcements > 0 && (
                  <Badge className="ml-1 sm:ml-2 bg-red-500 text-white h-5 min-w-5 flex items-center justify-center px-1.5 shrink-0">
                    {notifCounts.unread_announcements}
                  </Badge>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="channels" className="space-y-4">
            <Card className="glass-effect">
              <CardHeader>
                <CardTitle>Sélectionner un site</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={selectedSite} onValueChange={(value) => { setSelectedSite(value); loadChannelMessages(value); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {selectedSite && (
              <>
                <Card className="glass-effect">
                  <CardHeader>
                    <CardTitle>Discussion du site</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
                      {channelMessages.length === 0 ? (
                        <p className="text-center text-gray-500 dark:text-slate-400 py-8">Aucun message</p>
                      ) : (
                        channelMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`p-3 rounded-lg ${msg.from_user_id === user.id ? 'bg-blue-50 dark:bg-indigo-950/40 ml-8' : 'bg-gray-50 dark:bg-slate-800 mr-8'}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{msg.from_user_name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 dark:text-slate-400">
                                  {format(new Date(msg.created_at), 'dd/MM HH:mm', { locale: fr })}
                                </span>
                                {msg.read_by.length > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    <CheckCheck className="h-3 w-3 mr-1" />
                                    {msg.read_by.length}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <p className="text-gray-700 dark:text-slate-200 text-sm">{msg.content}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                {canWriteChannel ? (
                  <Card className="glass-effect">
                    <CardContent className="pt-6">
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Écrire un message..."
                          value={newChannelMessage}
                          onChange={(e) => setNewChannelMessage(e.target.value)}
                          rows={2}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendChannelMessage();
                            }
                          }}
                        />
                        <Button onClick={sendChannelMessage} className="btn-primary">
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="glass-effect">
                    <CardContent className="py-4 text-center">
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        Ce compte est en lecture seule sur les canaux d'équipe. Connectez-vous avec votre compte personnel pour écrire un message.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="private">
            <Card className="glass-effect overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-[600px]">
                <div className={`border-r border-slate-200 dark:border-slate-700 flex flex-col min-h-0 ${activeContactId ? 'hidden md:flex' : 'flex'}`}>
                  <div className="p-3 border-b border-slate-200 dark:border-slate-700 space-y-2">
                    <Select value={newConversationTarget} onValueChange={(v) => { setNewConversationTarget(''); openConversation(v); }}>
                      <SelectTrigger data-testid="new-conversation-select">
                        <SelectValue placeholder="+ Nouvelle conversation" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortByRoleOrder(users).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {ROLE_LABELS[u.role] || u.role} - {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="w-full" onClick={openBulkCompose} data-testid="bulk-compose-btn">
                      <UsersRound className="h-3.5 w-3.5 mr-2" />
                      Message à plusieurs
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto scrollbar-thin">
                    {conversations.length === 0 ? (
                      <p className="text-center text-sm text-gray-400 py-8 px-3">Aucune conversation</p>
                    ) : (
                      conversations.map((conv) => (
                        <button
                          key={conv.id}
                          type="button"
                          onClick={() => openConversation(conv.id)}
                          className={`w-full text-left p-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${activeContactId === conv.id ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''}`}
                          data-testid={`conversation-${conv.id}`}
                        >
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate">{conv.name}</span>
                            {conv.unread > 0 && (
                              <Badge className="bg-blue-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center px-1.5">{conv.unread}</Badge>
                            )}
                          </div>
                          <p className={`text-xs truncate ${conv.unread > 0 ? 'text-slate-700 dark:text-slate-200 font-medium' : 'text-slate-400'}`}>
                            {conv.lastMessage ? conv.lastMessage.content : 'Aucun message — cliquez pour écrire'}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className={`flex-col min-h-0 ${activeContactId ? 'flex' : 'hidden md:flex'}`}>
                  {!activeContactId ? (
                    <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                      Sélectionnez une conversation pour l'afficher ici
                    </div>
                  ) : (
                    <>
                      <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                        <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setActiveContactId(null)}>
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <span className="font-semibold text-slate-800 dark:text-slate-100">{activeContact?.name}</span>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
                        {activeMessages.length === 0 ? (
                          <p className="text-center text-sm text-gray-400 py-8">Aucun message pour l'instant — écrivez le premier !</p>
                        ) : (
                          activeMessages.map((msg) => (
                            <div key={msg.id} className={`max-w-[75%] ${msg.from_user_id === user.id ? 'ml-auto' : ''}`}>
                              <div className={`p-2.5 rounded-2xl text-sm ${msg.from_user_id === user.id ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-bl-sm'}`}>
                                {msg.content}
                              </div>
                              <p className={`text-[10px] text-slate-400 mt-0.5 ${msg.from_user_id === user.id ? 'text-right' : ''}`}>
                                {format(new Date(msg.created_at), 'dd/MM HH:mm', { locale: fr })}
                                {msg.from_user_id === user.id && msg.read && ' · Lu'}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="p-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                        <Input
                          placeholder="Votre message..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendReply(); } }}
                          data-testid="reply-input"
                        />
                        <Button onClick={sendReply} className="btn-primary shrink-0" data-testid="send-reply-btn">
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {canViewAnnouncements && (
            <TabsContent value="announcements" className="space-y-4">
              {isAdmin && (
                <Card className="glass-effect">
                  <CardContent className="pt-6">
                    {showAnnouncementForm ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <div className="w-24 shrink-0">
                            <Label className="text-xs">Couleur du tag</Label>
                            <input
                              type="color"
                              value={announcementTagColor}
                              onChange={(e) => setAnnouncementTagColor(e.target.value)}
                              className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 cursor-pointer"
                              data-testid="announcement-tag-color"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs">Tag (facultatif)</Label>
                            <Input
                              placeholder="Ex: URGENT, RH, Info..."
                              value={announcementTag}
                              onChange={(e) => setAnnouncementTag(e.target.value)}
                              data-testid="announcement-tag"
                            />
                          </div>
                        </div>
                        <Input
                          placeholder="Titre de l'annonce"
                          value={announcementTitle}
                          onChange={(e) => setAnnouncementTitle(e.target.value)}
                          data-testid="announcement-title"
                        />
                        <Textarea
                          placeholder="Contenu de l'annonce..."
                          value={announcementContent}
                          onChange={(e) => setAnnouncementContent(e.target.value)}
                          rows={4}
                          data-testid="announcement-content"
                        />
                        <div>
                          <Label className="text-xs">Pièce jointe (facultatif)</Label>
                          <FileUpload
                            onFilesUploaded={(fileIds) => setAnnouncementAttachments(fileIds)}
                            existingFiles={[]}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={resetAnnouncementForm}>Annuler</Button>
                          <Button className="btn-primary" onClick={sendAnnouncement} disabled={sendingAnnouncement} data-testid="publish-announcement-btn">
                            {sendingAnnouncement ? 'Enregistrement...' : (editingAnnouncementId ? 'Enregistrer' : 'Publier')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button className="btn-primary" onClick={() => setShowAnnouncementForm(true)} data-testid="new-announcement-btn">
                        <Megaphone className="h-4 w-4 mr-2" />
                        Publier une annonce
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {announcements.length === 0 ? (
                <Card className="glass-effect">
                  <CardContent className="py-12 text-center">
                    <Megaphone className="h-10 w-10 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-slate-300">Aucune annonce pour le moment</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {announcements.map((a) => {
                    const alreadyRead = a.read_by.includes(user.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => openAnnouncement(a)}
                        className={`w-full text-left card-hover glass-effect p-3 flex items-center justify-between gap-2 ${!alreadyRead ? 'ring-2 ring-indigo-400 bg-indigo-50/50 dark:bg-indigo-950/20' : ''}`}
                        data-testid={`announcement-${a.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {a.tag && (
                              <span
                                className="text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full shrink-0"
                                style={{ backgroundColor: a.tag_color || '#4F46E5' }}
                                data-testid={`announcement-tag-${a.id}`}
                              >
                                {a.tag}
                              </span>
                            )}
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{a.title}</p>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Par {a.created_by_name} · {format(new Date(a.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                          </p>
                        </div>
                        {!alreadyRead && (
                          <Badge className="bg-indigo-500 text-white text-xs shrink-0">Nouveau</Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Détail d'une annonce, ouverte depuis la liste - la mention "lu" est
          posée à ce moment précis (voir openAnnouncement) */}
      <Dialog open={!!viewingAnnouncementId} onOpenChange={(open) => !open && setViewingAnnouncementId(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {viewingAnnouncement && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {viewingAnnouncement.tag && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: viewingAnnouncement.tag_color || '#4F46E5' }}
                    >
                      {viewingAnnouncement.tag}
                    </span>
                  )}
                </div>
                <DialogTitle>{viewingAnnouncement.title}</DialogTitle>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Par {viewingAnnouncement.created_by_name} · {format(new Date(viewingAnnouncement.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                </p>
              </DialogHeader>

              <div className="space-y-3">
                <p className="text-sm text-gray-700 dark:text-slate-200 whitespace-pre-wrap">{viewingAnnouncement.content}</p>

                {viewingAnnouncement.attachments && viewingAnnouncement.attachments.length > 0 && (
                  <FileViewer fileIds={viewingAnnouncement.attachments} />
                )}

                {canSeeAnnouncementReadStatus && (
                  <Accordion type="single" collapsible>
                    <AccordionItem value="read-by">
                      <AccordionTrigger className="text-xs">
                        <span className="flex items-center gap-1.5">
                          <Eye className="h-3.5 w-3.5" />
                          Lu par {viewingAnnouncement.read_details?.length || 0} personne{(viewingAnnouncement.read_details?.length || 0) > 1 ? 's' : ''}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        {(viewingAnnouncement.read_details || []).length === 0 ? (
                          <p className="text-xs text-gray-500 dark:text-slate-400">Personne n'a encore lu cette annonce.</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {viewingAnnouncement.read_details.map((r, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs border rounded-lg p-2 bg-gray-50 dark:bg-slate-800">
                                <span className="font-medium">{r.user_name}</span>
                                <span className="text-gray-500 dark:text-slate-400">{format(new Date(r.read_at), 'dd/MM HH:mm', { locale: fr })}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}

                {isAdmin && (
                  <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setViewingAnnouncementId(null); openEditAnnouncement(viewingAnnouncement); }}
                      data-testid={`edit-announcement-${viewingAnnouncement.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => { deleteAnnouncement(viewingAnnouncement.id); setViewingAnnouncementId(null); }}
                      data-testid={`delete-announcement-${viewingAnnouncement.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Envoi d'un même message privé à plusieurs personnes, ou à un rôle
          en particulier d'un site en particulier */}
      <Dialog open={showBulkCompose} onOpenChange={setShowBulkCompose}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message à plusieurs destinataires</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={bulkMode === 'people' ? 'default' : 'outline'}
                size="sm"
                className={bulkMode === 'people' ? 'btn-primary flex-1' : 'flex-1'}
                onClick={() => setBulkMode('people')}
                data-testid="bulk-mode-people"
              >
                Choisir des personnes
              </Button>
              <Button
                type="button"
                variant={bulkMode === 'roleSite' ? 'default' : 'outline'}
                size="sm"
                className={bulkMode === 'roleSite' ? 'btn-primary flex-1' : 'flex-1'}
                onClick={() => setBulkMode('roleSite')}
                data-testid="bulk-mode-role-site"
              >
                Par rôle et site
              </Button>
            </div>

            {bulkMode === 'people' ? (
              <div className="space-y-1.5 max-h-56 overflow-y-auto scrollbar-thin border border-slate-200 dark:border-slate-700 rounded-lg p-2">
                {sortByRoleOrder(users).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                    <Checkbox
                      checked={bulkSelectedIds.includes(u.id)}
                      onCheckedChange={() => toggleBulkSelected(u.id)}
                      data-testid={`bulk-select-${u.id}`}
                    />
                    <span className="text-sm">{ROLE_LABELS[u.role] || u.role} - {u.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Rôle(s)</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin border border-slate-200 dark:border-slate-700 rounded-lg p-2">
                    {[...new Set(users.map(u => u.role))].sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b)).map((role) => (
                      <label key={role} className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                        <Checkbox
                          checked={bulkRoles.includes(role)}
                          onCheckedChange={() => toggleBulkRole(role)}
                          data-testid={`bulk-role-${role}`}
                        />
                        <span className="text-sm">{ROLE_LABELS[role] || role}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Site(s)</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin border border-slate-200 dark:border-slate-700 rounded-lg p-2">
                    {sites.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                        <Checkbox
                          checked={bulkSites.includes(s.id)}
                          onCheckedChange={() => toggleBulkSite(s.id)}
                          data-testid={`bulk-site-${s.id}`}
                        />
                        <span className="text-sm">{s.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 space-y-1">
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {bulkRoles.length === 0 || bulkSites.length === 0
                      ? 'Sélectionnez au moins un rôle et un site'
                      : `${bulkRoleSiteMatches.length} destinataire${bulkRoleSiteMatches.length > 1 ? 's' : ''} correspondant${bulkRoleSiteMatches.length > 1 ? 's' : ''} :`}
                  </p>
                  {bulkRoleSiteMatches.length > 0 && (
                    <div className="max-h-28 overflow-y-auto scrollbar-thin border border-slate-200 dark:border-slate-700 rounded-lg p-2 space-y-0.5">
                      {sortByRoleOrder(bulkRoleSiteMatches).map((u) => (
                        <p key={u.id} className="text-xs text-slate-700 dark:text-slate-200">
                          {ROLE_LABELS[u.role] || u.role} - {u.name}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <Input
              placeholder="Sujet (facultatif)"
              value={bulkSubject}
              onChange={(e) => setBulkSubject(e.target.value)}
              data-testid="bulk-subject"
            />
            <Textarea
              placeholder="Votre message..."
              value={bulkContent}
              onChange={(e) => setBulkContent(e.target.value)}
              rows={4}
              data-testid="bulk-content"
            />

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowBulkCompose(false)}>Annuler</Button>
              <Button className="flex-1 btn-primary" onClick={sendBulkMessage} disabled={bulkSending} data-testid="send-bulk-btn">
                {bulkSending ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MessagerieEvoluee;
